import { useState } from 'react';
import { Icon } from '@iconify/react';
import {
    ID_PROOF_LABEL,
    ID_PROOF_ICON,
    fmtDate,
    fmtDateTime,
    fmtPhone,
    maskIdNumber,
} from '../visitorConstants';

// The labelled cells that make up the visitor profile below the header.

const DetailRow = ({ icon, label, children, className = '' }) => (
    <div className={className}>
        <p className="tw-mb-0 tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-900 tw-font-bold">{label}</p>
        <div className="tw-text-xs tw-text-gray-800 tw-mt-1 tw-flex tw-items-center tw-gap-1.5">
            {icon && <Icon icon={icon} className="tw-text-gray-400 tw-shrink-0" />}
            {children}
        </div>
    </div>
);

const NotRecorded = () => <span className="tw-text-gray-400">Not recorded</span>;

// visit_count and last_visit_at are written by the visit flow, never by these
// screens — this strip is read-only by design.
export const VisitorActivityStrip = ({ visitor }) => (
    <div className="tw-px-5 tw-py-3 tw-bg-gray-50/60 tw-border-b tw-border-gray-100 tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-3">
        <DetailRow label="Total visits" icon="mdi:counter">
            <span className="tw-font-bold">{visitor.visit_count ?? 0}</span>
        </DetailRow>
        <DetailRow label="Last visit" icon="mdi:calendar-clock-outline">
            {fmtDate(visitor.last_visit_at)}
        </DetailRow>
        <DetailRow label="Society" icon="mdi:office-building-outline">
            {visitor.society?.name || `#${visitor.society_id}`}
        </DetailRow>
        <DetailRow label="Registered" icon="mdi:calendar-plus">
            {fmtDate(visitor.created_at)}
        </DetailRow>
    </div>
);

export const VisitorContactPanel = ({ visitor }) => {
    // ID numbers are personal data, so the tail shows by default and the full
    // value is revealed only on request.
    const [revealId, setRevealId] = useState(false);

    return (
        <div className="tw-p-5 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
            <DetailRow label="Phone" icon="mdi:phone-outline">
                <span className="tw-font-mono">{fmtPhone(visitor.phone)}</span>
            </DetailRow>

            <DetailRow label="Email" icon="mdi:email-outline">
                {visitor.email
                    ? <a href={`mailto:${visitor.email}`} className="hover:tw-text-sky-700 tw-truncate">{visitor.email}</a>
                    : <NotRecorded />}
            </DetailRow>

            <DetailRow label="Company" icon="mdi:domain">
                {visitor.company_name || <NotRecorded />}
            </DetailRow>

            <DetailRow
                label="ID proof"
                icon={ID_PROOF_ICON[visitor.id_proof_type] || 'mdi:card-account-details-outline'}
            >
                {visitor.id_proof_type ? (
                    <span className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                        <span>{ID_PROOF_LABEL[visitor.id_proof_type] || visitor.id_proof_type}</span>
                        {visitor.id_proof_number && (
                            <>
                                <span className="tw-font-mono tw-text-gray-600">
                                    {revealId ? visitor.id_proof_number : maskIdNumber(visitor.id_proof_number)}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setRevealId(r => !r)}
                                    title={revealId ? 'Hide number' : 'Show number'}
                                    className="tw-text-gray-400 hover:tw-text-gray-700"
                                >
                                    <Icon icon={revealId ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} className="tw-text-sm" />
                                </button>
                            </>
                        )}
                    </span>
                ) : <NotRecorded />}
            </DetailRow>

            {visitor.address && (
                <DetailRow label="Address" icon="mdi:map-marker-outline" className="sm:tw-col-span-2">
                    <span className="tw-whitespace-pre-wrap">{visitor.address}</span>
                </DetailRow>
            )}
        </div>
    );
};

export const VisitorNotesPanel = ({ notes }) => {
    if (!notes) return null;
    return (
        <div className="tw-px-5 tw-pb-5">
            <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-900 tw-font-bold tw-mb-1.5">
                Internal notes
            </p>
            <div className="tw-px-3 tw-py-2.5 tw-bg-amber-50/60 tw-border tw-border-amber-100 tw-rounded-lg tw-text-xs tw-text-gray-700 tw-whitespace-pre-wrap">
                {notes}
            </div>
            <p className="tw-mb-0 tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-flex tw-items-center tw-gap-1">
                <Icon icon="mdi:lock-outline" />
                Visible to society staff only
            </p>
        </div>
    );
};

// uuid is exposed by the API but no endpoint looks it up yet, so it is shown
// for support and debugging rather than offered as a link.
export const VisitorRecordTrail = ({ visitor }) => (
    <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50/40 tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-text-[10px] tw-text-gray-500">
        <span className="tw-flex tw-items-center tw-gap-1">
            <Icon icon="mdi:identifier" className="tw-text-gray-400" />
            ID {visitor.visitor_id}
        </span>
        {visitor.uuid && (
            <span className="tw-flex tw-items-center tw-gap-1 tw-font-mono" title={visitor.uuid}>
                <Icon icon="mdi:fingerprint" className="tw-text-gray-400" />
                {visitor.uuid}
            </span>
        )}
        <span className="tw-flex tw-items-center tw-gap-1">
            <Icon icon="mdi:clock-edit-outline" className="tw-text-gray-400" />
            Updated {fmtDateTime(visitor.updated_at)}
        </span>
    </div>
);

export default DetailRow;
